import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import type { TAttentionStatusActionId } from 'partner360/entities/attentionStatuses'

import type { IFinancialCockpitUserWithScope } from '../../../api/types'
import { selectCockpitManagerUsers, selectCockpitPbmUsers } from '../../../model/selectors'
import type { IAttentionStatusesReminder } from '../types'

import { useAttentionCardPartner } from './useAttentionCardPartner'
import { type TAttentionReminderBlock, useAttentionReminder } from './useAttentionReminder'
import { useAttentionReminderScope } from './useAttentionReminderScope'

/**
 * One recipient per person and partner: in a card over several partners the same PBM is reminded
 * about each of their partners.
 */
const toRecipients = (
  users: IFinancialCockpitUserWithScope[],
  partnerId?: string
): IAttentionStatusesReminder[] => {
  const seen = new Set<string>()

  return users
    .filter(({ PartnerId, Email }) => {
      const key = `${PartnerId}:${Email}`
      if ((partnerId && PartnerId !== partnerId) || !Email || seen.has(key)) {
        return false
      }

      seen.add(key)
      return true
    })
    .map(({ Email, FullName, PartnerId }) => ({
      email: Email,
      name: FullName?.trim() || Email,
      partnerId: PartnerId || undefined,
    }))
}

export interface IAttentionStatusReminders {
  handlers: Partial<Record<TAttentionStatusActionId, () => void>>
  /** Why a reminder cannot go out now, per reminder; a reminder missing here can be sent. */
  blocks: Partial<Record<TAttentionStatusActionId, TAttentionReminderBlock>>
  /** Reminders the service has not answered about yet; they wait rather than offer a send. */
  checking: TAttentionStatusActionId[]
}

/**
 * The status card's own reminders, for the corner of a section that does not draw them itself.
 * They reach the people the grouped control names — the card's partner's PBMs and managers, or
 * every partner's in a card over several — one send per partner. `reminder/check` is asked only for
 * the reminders the reader is offered (`shown`).
 */
export const useAttentionStatusReminders = (
  bitValue: number,
  shown: TAttentionStatusActionId[] = []
): IAttentionStatusReminders => {
  const { partnerId } = useAttentionCardPartner()
  const pbmUsers = useSelector(selectCockpitPbmUsers)
  const managerUsers = useSelector(selectCockpitManagerUsers)
  const scope = useAttentionReminderScope()

  const pbms = useMemo(() => toRecipients(pbmUsers, partnerId), [partnerId, pbmUsers])
  const managers = useMemo(() => toRecipients(managerUsers, partnerId), [managerUsers, partnerId])
  const pbmRoute = useMemo(() => ({ bitValue, actionId: 'remindPbm' as const }), [bitValue])
  const managerRoute = useMemo(() => ({ bitValue, actionId: 'remindManager' as const }), [bitValue])

  const pbmReminder = useAttentionReminder({
    recipients: pbms,
    route: pbmRoute,
    scope,
    enabled: shown.includes('remindPbm'),
  })
  const managerReminder = useAttentionReminder({
    recipients: managers,
    route: managerRoute,
    scope,
    enabled: shown.includes('remindManager'),
  })
  const { remind: remindPbm, block: pbmBlock, isChecking: isCheckingPbm } = pbmReminder
  const {
    remind: remindManager,
    block: managerBlock,
    isChecking: isCheckingManager,
  } = managerReminder

  return useMemo(
    () => ({
      handlers: {
        remindPbm: () => void remindPbm(),
        remindManager: () => void remindManager(),
      },
      blocks: {
        ...(pbmBlock ? { remindPbm: pbmBlock } : {}),
        ...(managerBlock ? { remindManager: managerBlock } : {}),
      },
      checking: [
        ...(isCheckingPbm ? (['remindPbm'] as const) : []),
        ...(isCheckingManager ? (['remindManager'] as const) : []),
      ],
    }),
    [isCheckingManager, isCheckingPbm, managerBlock, pbmBlock, remindManager, remindPbm]
  )
}
